package fr.manyverse.app.presentation.components.character_sheet

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_sheet_show_less
import manyverse.shared.generated.resources.character_sheet_show_more
import org.jetbrains.compose.resources.stringResource

/** Tags de test du bouton de dépliage, exposés pour distinguer les deux états sans dépendre de la langue. */
const val TAG_SHOW_MORE = "descriptionShowMore"
const val TAG_SHOW_LESS = "descriptionShowLess"

/**
 * Description facultative affichée sous un élément de fiche (caractéristique, compétence, pouvoir).
 *
 * Règles d'affichage :
 * - [description] absente ou vide ⇒ rien n'est composé (aucune ligne, aucun espacement) ;
 * - description tenant sur une seule ligne ⇒ affichée en entier, sans bouton ;
 * - description débordant ⇒ première ligne seulement, avec un bouton icône de dépliage **sur la
 *   même ligne** que le texte, pour ne pas consommer de hauteur supplémentaire.
 */
@Composable
fun ExpandableDescription(
    description: String?,
    modifier: Modifier = Modifier
) {
    val text = description?.trim().orEmpty()
    if (text.isEmpty()) return

    var expanded by remember(text) { mutableStateOf(false) }
    var hasOverflow by remember(text) { mutableStateOf(false) }

    Row(
        modifier = modifier.fillMaxWidth(),
        // Replié, le texte tient sur une ligne et se centre face à l'icône ; déplié, l'icône reste
        // alignée sur la première ligne plutôt que de flotter au milieu du paragraphe.
        verticalAlignment = if (expanded) Alignment.Top else Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = if (expanded) Int.MAX_VALUE else 1,
            overflow = TextOverflow.Ellipsis,
            // Replié uniquement : une fois déplié il n'y a plus de débordement, et remettre le
            // drapeau à `false` ferait disparaître le bouton de repliage.
            onTextLayout = { result -> if (!expanded) hasOverflow = result.hasVisualOverflow },
            modifier = Modifier.weight(1f)
        )
        if (hasOverflow) {
            IconButton(
                onClick = { expanded = !expanded },
                modifier = Modifier
                    .size(24.dp)
                    .testTag(if (expanded) TAG_SHOW_LESS else TAG_SHOW_MORE)
            ) {
                Icon(
                    imageVector = if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = stringResource(
                        if (expanded) Res.string.character_sheet_show_less
                        else Res.string.character_sheet_show_more
                    ),
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}
